import * as React from 'react'
import { useState, useCallback, useEffect, useRef } from 'react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import * as styles from './Tooltip.css'

// =============================================================================
// Tooltip Provider
// =============================================================================

type TooltipProviderProps = TooltipPrimitive.TooltipProviderProps

const TooltipProvider = TooltipPrimitive.Provider

// =============================================================================
// Tooltip Root
// =============================================================================

type TooltipRootProps = TooltipPrimitive.TooltipProps

const TooltipRoot = TooltipPrimitive.Root

// =============================================================================
// Tooltip Trigger
// =============================================================================

type TooltipTriggerProps = TooltipPrimitive.TooltipTriggerProps

const TooltipTrigger = TooltipPrimitive.Trigger

// =============================================================================
// Tooltip Portal
// =============================================================================

type TooltipPortalProps = TooltipPrimitive.TooltipPortalProps

const TooltipPortal = TooltipPrimitive.Portal

// =============================================================================
// Tooltip Content
// =============================================================================

type TooltipContentProps = TooltipPrimitive.TooltipContentProps & {
  showArrow?: boolean
}

const TooltipContent = React.forwardRef<
  React.ComponentRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(({ className, sideOffset = 4, showArrow = true, children, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={`${styles.content} ${className ?? ''}`}
    {...props}
  >
    {children}
    {showArrow && <TooltipPrimitive.Arrow className={styles.arrow} />}
  </TooltipPrimitive.Content>
))

TooltipContent.displayName = 'Tooltip.Content'

// =============================================================================
// Simple Tooltip (convenience component)
// =============================================================================

type SimpleTooltipProps = {
  content: React.ReactNode
  children: React.ReactNode
  side?: TooltipContentProps['side']
  align?: TooltipContentProps['align']
  delayDuration?: number
  showArrow?: boolean
}

const SimpleTooltip: React.FC<SimpleTooltipProps> = ({
  content,
  children,
  side = 'top',
  align = 'center',
  delayDuration,
  showArrow = true,
}) => {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  // Handle click/tap to toggle tooltip (for touch devices)
  const handleClick = useCallback((e: React.MouseEvent) => {
    // Prevent the click from bubbling (e.g., if inside a button)
    e.preventDefault()
    e.stopPropagation()
    setOpen((prev) => !prev)
  }, [])

  // Close tooltip when clicking outside
  useEffect(() => {
    if (!open) return

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node
      const trigger = triggerRef.current
      const content = contentRef.current

      // Don't close if clicking the trigger or content
      if (trigger?.contains(target) || content?.contains(target)) {
        return
      }

      setOpen(false)
    }

    // Use a small delay to avoid closing immediately on the same tap that opened it
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('touchstart', handleClickOutside)
    }, 0)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [open])

  return (
    <TooltipRoot open={open} onOpenChange={setOpen} delayDuration={delayDuration}>
      <TooltipTrigger asChild ref={triggerRef} onClick={handleClick}>
        {children}
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent ref={contentRef} side={side} align={align} showArrow={showArrow}>
          {content}
        </TooltipContent>
      </TooltipPortal>
    </TooltipRoot>
  )
}

// =============================================================================
// Exports
// =============================================================================

export const Tooltip = Object.assign(SimpleTooltip, {
  Provider: TooltipProvider,
  Root: TooltipRoot,
  Trigger: TooltipTrigger,
  Portal: TooltipPortal,
  Content: TooltipContent,
})

export type {
  TooltipProviderProps,
  TooltipRootProps,
  TooltipTriggerProps,
  TooltipPortalProps,
  TooltipContentProps,
  SimpleTooltipProps,
}
